/**
 * Der Seitenkopf - EINE Bauart fuer alle sieben Seiten des Werkzeugs.
 *
 * ================= BEWUSSTE ABWEICHUNG VOM BESTAND (2026-08-13) =================
 *
 * Diese Komponente ersetzt VIER verschiedene Kopf-Bauarten, die `index.html` nebeneinander fuehrte,
 * und traegt sie auf eine Seite, die vorher gar keinen Kopf hatte:
 *
 *   Bestellboard   `<span class="order-list-title">` INNERHALB der Werkzeugleiste, .88rem
 *   Zeitstrahl     `.ov-page-head > .ov-title`, 1.12rem, ohne Unterzeile
 *   Uebersicht     `.ops-hero`: Eyebrow + `<h1>` 1.42rem + Fliesstext + Datumskachel
 *   Zahlungen      `.ov-page-head > div > (.ov-title, .cost-count)`, 1.12rem
 *   Kosten         `.ov-page-head > .ov-title`, 1.12rem, ohne Unterzeile
 *   Nutzer         `.users-head > div > (h1, p)`, 1.45rem
 *   Lieferanten    gar keiner - die Seite begann mit dem JTL-Suchfeld
 *
 * Drei Bauarten, drei Schriftgroessen, und nur zwei der sieben Seiten hatten oben ueberhaupt eine
 * `<h1>`. Der Auftraggeber hat am 2026-08-13 entschieden, das zu vereinheitlichen - ausdruecklich in
 * Kauf nehmend, dass die optische Paritaet zu `index.html` damit an dieser Stelle endet.
 *
 * WO DIE ABWEICHUNG FESTGEHALTEN IST - drei Orte, keiner davon blosse Prosa:
 *  1. `test/visual/ausnahmen.ts`, Eintrag `Seitenkopf` (samt `ALLE_FLAECHEN` in `ausnahmen.test.ts`)
 *  2. `test/komponenten/App/seitenkopf.test.tsx` - der Waechter, der die Vereinheitlichung haelt
 *  3. `docs/superpowers/decisions/2026-08-13-einheitlicher-seitenkopf.md`
 *
 * Die fuenf strukturellen Paritaetstests (Board, Uebersicht, Zahlungsansicht, Zahlungen, Nutzer)
 * schneiden den Kopf seither auf BEIDEN Seiten heraus und pruefen ausdruecklich nach, WELCHE
 * Bestandsknoten sie herausschneiden - siehe `test/komponenten/bewussterSeitenkopf.ts`. Alles
 * unterhalb des Kopfes bleibt Knoten fuer Knoten geprueft.
 *
 * ================= Die Regel fuer den Titel =================
 *
 * Der Titel ist WOERTLICH die Beschriftung des Navigationsreiters (`App/Navigation.tsx`, `REITER`).
 * Was im Bestand zusaetzlich im Titel stand („· Transport", „· offene Zahlungen", „& Historie"),
 * steht jetzt in der Unterzeile. Grund: ein Reiter und die Ueberschrift, zu der er fuehrt, sollen
 * dasselbe Wort tragen - im Bestand hiess der Reiter „Zahlungen & Kurssicherung" und die Seite
 * darunter „Kurssicherung & Zahlungsbedarf".
 *
 * Die Eyebrow-Zeile „Operations Cockpit" der Uebersicht faellt ersatzlos weg (Auftraggeber,
 * 2026-08-13): sie sagte nichts, was der markierte Navigationsreiter nicht schon zeigt, und waere
 * auf den sechs anderen Seiten eine erfundene Kategorie gewesen.
 */
import type { ReactNode } from "react";

export interface SeitenkopfProps {
  /** Die Ueberschrift - woertlich die Beschriftung des Navigationsreiters, siehe Kopfkommentar. */
  titel: string;
  /**
   * Eine Zeile unter dem Titel: was die Seite gerade zeigt. Fehlt sie oder ist sie leer, faellt der
   * Knoten ganz weg - eine leere Unterzeile wuerde sonst Hoehe belegen, die keiner sieht.
   */
  unterzeile?: string;
  /**
   * Rechter Steckplatz fuer das, was eine Seite sonst noch oben braucht: Suche und Sortierung des
   * Zeitstrahls, die Datumskachel der Uebersicht. Ohne Inhalt faellt auch dieser Knoten weg.
   */
  children?: ReactNode;
}

export function Seitenkopf({ titel, unterzeile, children }: SeitenkopfProps) {
  return (
    <div className="seitenkopf">
      <div className="seitenkopf-text">
        <h1 className="seitenkopf-titel">{titel}</h1>
        {unterzeile ? <p className="seitenkopf-unterzeile">{unterzeile}</p> : null}
      </div>
      {children ? <div className="seitenkopf-extra">{children}</div> : null}
    </div>
  );
}
